/**
 * fareRulesRouter.js — Supplier-aware fare-rules dispatch.
 *
 * Sprint-5 · FLIGHT_SEARCH_RESULTS_COMPLETE_DEVELOPER_REPORT.md S1/R1/I1.
 * Previously every search-results page called `/api/tripjack/fare-rules/{id}`
 * regardless of `flight.source` — Riya rows failed (their priceId is the
 * Riya FlightID, not a Tripjack key) and offline rows triggered nonsense
 * lookups. This util centralises the branching so every page benefits.
 *
 * Backend BFFs in use:
 *   • Tripjack: GET /api/tripjack/fare-rules/{priceId}?flow_type=SEARCH
 *   • Riya:     GET /api/riya/fare-rule/{flightId}?track_id=...
 *   • Offline:  no supplier call; rules come from the booking record.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const fetchFareRulesForFlight = async (flight, priceIdOverride) => {
  const src = String(flight?.source || '').toLowerCase();
  const priceId = priceIdOverride || flight?.priceId || flight?.id;

  if (src === 'riya') {
    const flightId = flight?.riyaFlightId || flight?.id;
    const trackId = flight?.riyaTrackId || '';
    if (!flightId) {
      return { data: { success: false, message: 'Riya flight id missing' } };
    }
    return axios.get(`${API}/riya/fare-rule/${flightId}`, {
      params: { track_id: trackId },
    });
  }

  if (src === 'offline') {
    // Offline inventory ships with rules text on the flight record (or
    // configured via admin). No supplier round-trip.
    return {
      data: {
        success: true,
        _source: 'offline',
        rules: flight?.offlineRules || flight?.rules || (
          'Cancellation, refund and change fees are governed by the airline at '
          + 'the time of issuance. Please refer to the booking confirmation '
          + 'or contact support for the latest rules on this fare.'
        ),
      },
    };
  }

  // Tripjack (default + synthetic combo ids fall through here; result
  // pages should guard against `riya-combo-*` etc. before calling).
  if (!priceId) {
    return { data: { success: false, message: 'Price id missing' } };
  }
  return axios.get(`${API}/tripjack/fare-rules/${priceId}`);
};
